// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import React, {
  ChangeEvent,
  forwardRef,
  ReactNode,
  Ref,
  useEffect,
  useRef,
  useState
} from 'react';

import { BaseProps } from '../Base';
import InputWrapper from './InputWrapper';
import {
  StyledClear,
  StyledPasswordVisibilityToggleButton,
  StyledInput
} from './Styled';
import Clear from '../../icons/Clear';
import { RefCallBack } from 'react-hook-form';
import EyeRegular from '../../icons/EyeRegular';
import EyeSlashRegular from '../../icons/EyeSlashRegular';

export type Size = 'sm' | 'md';

export interface InputProps
  extends Omit<
      React.InputHTMLAttributes<HTMLInputElement>,
      'onChange' | 'value' | 'css'
    >,
    BaseProps {
  /** The callback fired when the state is changed. */
  onChange(event: ChangeEvent): void;
  /** The callback fired when the input value is cleared. */
  onClear?(): void;
  /** パスワードの表示が切り替えれた後のコールバック */
  onPasswordVisibilityChanged?: (state: boolean) => void;
  /** The icon in the input. */
  leadingIcon?: ReactNode;
  onLeadingIconClick?(): void;
  /** The size of the input. Default value is `md`. */
  sizing?: Size;
  /** The value of the input. */
  value: string;
  /** The id of the input. */
  id?: string;
  /** Whether or not the clear icon is shown, it defaults to `true`. */
  showClear?: boolean;
  /** パスワードの表示が切り替えれるかどうかのフラグ */
  passwordVisibilityTogglable?: boolean;
  /** React-hook-formのRef */
  rhfRef?: RefCallBack;
}

export const Input = forwardRef(
  (props: InputProps, externalRef: Ref<HTMLInputElement>) => {
    const {
      type,
      value,
      sizing,
      onClear,
      onPasswordVisibilityChanged,
      onChange,
      className,
      leadingIcon,
      onLeadingIconClick,
      showClear = true,
      passwordVisibilityTogglable,
      autoFocus,
      rhfRef,
      ...rest
    } = props;
    const [focused, setFocused] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const focusedRef = useRef(false);
    const internalRef = useRef(null);
    const inputRef = (externalRef ||
      internalRef) as React.MutableRefObject<HTMLInputElement>;
    const clearRef = useRef<HTMLButtonElement>(null);
    const passwordVisibilityToggleButtonRef = useRef<HTMLButtonElement>(null);
    const [readOnly, setReadOnly] = useState<boolean | undefined>(
      rest.readOnly || autoFocus
    );
    const showPasswordVisibilityToggleButton =
      passwordVisibilityTogglable && type === 'password';

    const label = props['aria-label']
      ? `clear ${props['aria-label']}`
      : 'clear';

    const handleClear = () => {
      if (onClear) {
        onClear();
        return;
      }

      const input = inputRef.current;
      const nativeSetter = Object.getOwnPropertyDescriptor(
        window.HTMLInputElement.prototype,
        'value'
      )?.set;

      if (nativeSetter && input) {
        nativeSetter.call(input, '');
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }

      input.focus();
    };

    const handlePasswordVisibilityChanged = () => {
      const newShowPasswordState = !showPassword;
      setShowPassword(newShowPasswordState);
      onPasswordVisibilityChanged?.(newShowPasswordState);
    };

    useEffect(() => {
      if (autoFocus) {
        setTimeout(() => setReadOnly(props.readOnly), 200);
        inputRef.current.focus();
      }
    }, [autoFocus]);

    useEffect(() => {
      let blurring = false;

      const onFocus = (e: any) => {
        if (!focusedRef.current) {
          return;
        }

        if (
          e.target !== clearRef.current &&
          e.target !== passwordVisibilityToggleButtonRef.current &&
          e.target !== inputRef.current
        ) {
          focusedRef.current = false;
          setFocused(false);
          return;
        }

        if (blurring) {
          blurring = false;
        }
      };

      const onFocusOut = (): void => {
        if (!focusedRef.current) {
          return;
        }

        blurring = true;
        setTimeout(() => {
          if (blurring) {
            focusedRef.current = false;
            setFocused(false);
          }

          blurring = false;
        }, 10);
      };

      document.addEventListener('focusin', onFocus);
      document.addEventListener('focusout', onFocusOut);

      return () => {
        document.removeEventListener('focusin', onFocus);
        document.removeEventListener('focusout', onFocusOut);
      };
    }, []);
    return (
      <InputWrapper
        leadingIcon={leadingIcon}
        onLeadingIconClick={onLeadingIconClick}
        sizing={sizing}
        hasPasswordVisibilityToggleButton={showPasswordVisibilityToggleButton}
        className={`ch-input-wrapper ${className || ''}`}
      >
        <StyledInput
          {...rest}
          readOnly={readOnly}
          value={value}
          type={
            showPasswordVisibilityToggleButton && showPassword ? 'text' : type
          }
          ref={(e) => {
            rhfRef?.(e);
            inputRef.current = e as HTMLInputElement;
          }}
          className="ch-input"
          onChange={onChange}
          data-testid="input"
          onFocus={() => {
            focusedRef.current = true;
            setFocused(true);
          }}
        />
        {showPasswordVisibilityToggleButton && (
          <StyledPasswordVisibilityToggleButton
            type="button"
            active={!!value?.length}
            tabIndex={-1}
            aria-label={label}
            onClick={handlePasswordVisibilityChanged}
            ref={passwordVisibilityToggleButtonRef}
          >
            {showPassword ? (
              <EyeRegular width="1.25rem" />
            ) : (
              <EyeSlashRegular width="1.25rem" />
            )}
          </StyledPasswordVisibilityToggleButton>
        )}
        {showClear && (
          <StyledClear
            type="button"
            active={!!(onClear || value?.length)}
            tabIndex={-1}
            aria-label={label}
            onClick={handleClear}
            ref={clearRef}
          >
            <Clear width="1.25rem" />
          </StyledClear>
        )}
      </InputWrapper>
    );
  }
);

Input.displayName = 'Input';

export default Input;
